import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { BACKEND } from "../../../config/configEnvs";
import ModalInDrawer from "../../ui/ModalInDrawer";
import LoadingScreen from "../../ui/temp-screens/LoadingScreen";
import MainSaveButtonFullWidth from "../../ui/components/MainSaveButtonFullWidth";

const getTodayISODate = () => new Date().toISOString().split("T")[0];

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("es-PE", { year: "numeric", month: "2-digit", day: "2-digit" });
};

const MAX_IMAGES = 5;

const toAbsoluteMediaUrl = (url) => {
  if (!url) return null;
  if (typeof url !== "string") return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${BACKEND}${url}`;
};

const OpenClientReviewsRequests = ({ open, onClose, serviceId }) => {
  const [loading, setLoading] = useState(false);
  const [serviceData, setServiceData] = useState({});

  const [updating, setUpdating] = useState(false);

  const [newDate, setNewDate] = useState(getTodayISODate());
  const [newDetails, setNewDetails] = useState("");
  const [newImages, setNewImages] = useState([]); // [{ file: File, previewUrl: string }]

  const getServiceData = async () => {
    if (!serviceId) return;
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND}/api/serviciosCotizaciones/${serviceId}?depth=3`);
      setServiceData(response.data || {});
    } catch (error) {
      toast.error("Error al obtener las solicitudes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      getServiceData();
      setNewDate((prev) => prev || getTodayISODate());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, serviceId]);

  useEffect(() => {
    if (!open) {
      // Evitar leaks de memoria por previews (ObjectURL) si se cierra el modal
      setNewImages((prev) => {
        prev.forEach((x) => {
          if (x?.previewUrl) URL.revokeObjectURL(x.previewUrl);
        });
        return [];
      });
    }
  }, [open]);

  const requests = useMemo(() => {
    return Array.isArray(serviceData?.requestClientReviews)
      ? serviceData.requestClientReviews
      : [];
  }, [serviceData]);

  const resetForm = () => {
    setNewDate(getTodayISODate());
    setNewDetails("");
    setNewImages((prev) => {
      prev.forEach((x) => {
        if (x?.previewUrl) URL.revokeObjectURL(x.previewUrl);
      });
      return [];
    });
  };

  const onPickImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const invalid = files.find((f) => !f?.type?.startsWith("image/"));
    if (invalid) {
      toast.warning("Solo se permiten imágenes");
      e.target.value = "";
      return;
    }

    setNewImages((prev) => {
      const remaining = MAX_IMAGES - prev.length;
      if (remaining <= 0) {
        toast.info(`Máximo ${MAX_IMAGES} imágenes`);
        return prev;
      }

      const picked = files.slice(0, remaining);
      if (files.length > remaining) {
        toast.info(`Solo se subirán ${remaining} imagen(es). Máximo ${MAX_IMAGES}.`);
      }

      return [
        ...prev,
        ...picked.map((file) => ({
          file,
          previewUrl: URL.createObjectURL(file),
        })),
      ];
    });

    // Permite volver a seleccionar el mismo archivo si el usuario lo desea
    e.target.value = "";
  };

  const removePickedImage = (idx) => {
    setNewImages((prev) => {
      const copy = [...prev];
      const removed = copy[idx];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      copy.splice(idx, 1);
      return copy;
    });
  };

  const uploadImagesRequestClientReviews = async (items) => {
    if (!items?.length) return [];
    const ids = [];

    for (const item of items.slice(0, MAX_IMAGES)) {
      const file = item?.file;
      if (!file) continue;
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${BACKEND}/api/imagesRequestClientReviews`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const id = response?.data?.doc?.id || response?.data?.id;
      if (!id) {
        throw new Error("No se pudo obtener el ID de la imagen subida");
      }
      ids.push(id);
    }

    return ids;
  };

  const createRequest = async () => {
    try {
      if (!serviceId) return;
      if (!newDate || !newDetails.trim()) {
        toast.warning("Completa fecha y detalles");
        return;
      }
      setUpdating(true);

      // 1) Subir imágenes (Payload) para luego guardar IDs como relationship hasMany
      const imageIds = await uploadImagesRequestClientReviews(newImages);

      // Leer de nuevo para evitar pisar cambios concurrentes
      const response = await axios.get(`${BACKEND}/api/serviciosCotizaciones/${serviceId}?depth=0`);
      const current = response.data || {};
      const currentArr = Array.isArray(current.requestClientReviews)
        ? [...current.requestClientReviews]
        : [];

      currentArr.push({
        date: newDate,
        details: newDetails.trim(),
        image: imageIds,
      });

      await axios.put(`${BACKEND}/api/serviciosCotizaciones/${serviceId}`, {
        requestClientReviews: currentArr,
      });

      toast.success("Solicitud creada");
      resetForm();
      await getServiceData();
    } catch (error) {
      toast.error("Error al crear la solicitud");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <ModalInDrawer
      activeModal={open}
      onClose={onClose}
      title="Revisiones al cliente"
      centered
      className="max-w-6xl"
      scrollContent={false}
    >
      {loading ? (
        <div className="h-[15rem]">
          <LoadingScreen text="Cargando..." />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Lista */}
          <div className="w-full overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-2 w-[140px]">Fecha</th>
                  <th className="px-3 py-2">Detalles</th>
                  <th className="px-3 py-2 w-[180px]">Fotos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-4 text-gray-500">
                      No hay solicitudes registradas.
                    </td>
                  </tr>
                ) : (
                  requests
                    .slice()
                    .reverse()
                    .map((r, idx) => (
                      <tr key={r?.id || `${r?.date}-${idx}`} className="hover:bg-gray-50">
                        <td className="px-3 py-2 whitespace-nowrap">
                          {formatDate(r?.date)}
                        </td>
                        <td className="px-3 py-2 whitespace-pre-wrap">
                          {r?.details || "-"}
                        </td>
                        <td className="px-3 py-2">
                          {Array.isArray(r?.image) && r.image.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {r.image.slice(0, 6).map((img, i2) => {
                                const url =
                                  typeof img === "object" && img !== null
                                    ? toAbsoluteMediaUrl(
                                        img?.url ||
                                          img?.sizes?.thumbnail?.url ||
                                          img?.sizes?.card?.url
                                      )
                                    : null;

                                return url ? (
                                  <a
                                    key={img?.id || `${idx}-${i2}`}
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="block"
                                    title="Abrir imagen"
                                  >
                                    <img
                                      src={url}
                                      alt="foto"
                                      className="h-10 w-10 object-cover rounded border border-gray-200"
                                    />
                                  </a>
                                ) : (
                                  <span
                                    key={`${idx}-${i2}`}
                                    className="text-[11px] text-gray-500"
                                  >
                                    ID
                                  </span>
                                );
                              })}
                              {r.image.length > 6 ? (
                                <span className="text-[11px] text-gray-500">
                                  +{r.image.length - 6}
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>

          {/* Crear nuevo */}
          <div className="border border-gray-200 rounded-lg p-3 bg-white">
            <div className="text-xs font-semibold text-gray-700 mb-2">
              Crear nueva solicitud
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex flex-col w-full sm:w-[220px]">
                  <label className="text-xs text-gray-600 mb-1">Fecha</label>
                  <input
                    type="date"
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                    className="text-xs rounded-md bg-gray-50 border border-gray-300 p-2 outline-none"
                  />
                </div>

                <div className="flex flex-col w-full">
                  <label className="text-xs text-gray-600 mb-1">Detalles</label>
                  <textarea
                    value={newDetails}
                    onChange={(e) => setNewDetails(e.target.value)}
                    rows={1}
                    className="text-xs rounded-md bg-gray-50 border border-gray-300 p-2 outline-none resize-none"
                    placeholder="Escribe el detalle de la solicitud..."
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex flex-col">
                  <label className="text-xs text-gray-600 mb-1">
                    Fotos (opcional){" "}
                    <span className="text-gray-400">({newImages.length}/{MAX_IMAGES})</span>
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onPickImages}
                    className="text-xs rounded-md bg-gray-50 border border-gray-300 p-2 outline-none"
                    disabled={updating}
                  />
                </div>

                {newImages.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {newImages.map((item, i) => (
                      <div
                        key={`${item?.file?.name || "img"}-${i}`}
                        className="relative"
                      >
                        <img
                          src={item.previewUrl}
                          alt="preview"
                          className="h-14 w-14 object-cover rounded border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => removePickedImage(i)}
                          className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-gray-800 text-white text-[10px] flex items-center justify-center"
                          title="Quitar"
                          disabled={updating}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              <MainSaveButtonFullWidth
                loading={updating}
                text="Registrar solicitud"
                onClick={createRequest}
                disabled={updating || !newDate || !newDetails.trim()}
                marginTop="0"
              />
            </div>
          </div>
        </div>
      )}
    </ModalInDrawer>
  );
};

export default OpenClientReviewsRequests;